// Cache global para fontes - persiste entre navegações e remontagens
// Modificado para não usar URL.createObjectURL, o que evita a criação de arquivos no painel Sources
const globalFontCache = new Map<string, { fontFace: FontFace }>();

// Adiciona um listener para limpar o cache quando a página for fechada ou recarregada
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    globalFontCache.forEach(({ fontFace }) => {
      document.fonts.delete(fontFace);
    });
  });
}

/**
 * Hook para gerenciamento de cache das fontes
 */
export function useFontCache() {
  // Nome do cache usado para armazenar as fontes
  const CACHE_NAME = 'font-cache';

  /**
   * Carrega uma fonte do cache e a adiciona ao documento
   * Usa um cache global para evitar criar múltiplas instâncias da mesma fonte
   * 
   * IMPORTANTE: Este método foi modificado para evitar URL.createObjectURL
   * A solução atual usa arrayBuffer diretamente, o que impede que os arquivos
   * apareçam no painel Sources do navegador. NÃO REVERTER para URL.createObjectURL.
   */
  const loadFontFromCache = async (fontName: string): Promise<boolean> => {
    try {
      // Verifica se a fonte já está no cache global
      if (globalFontCache.has(fontName)) {
        // Verifica se a fonte já está carregada no documento
        const { fontFace } = globalFontCache.get(fontName)!;
        
        // Se a fonte não estiver no document.fonts, adiciona novamente
        if (!document.fonts.has(fontFace)) {
          document.fonts.add(fontFace);
        }
        
        return true;
      }
      
      const fontCache = await caches.open(CACHE_NAME);
      const fontResponse = await fontCache.match(`/fonts/${fontName}`);
      
      if (fontResponse) {
        // Obtém o blob da fonte
        const fontBlob = await fontResponse.blob();
        
        /**
         * SOLUÇÃO PARA EVITAR ARQUIVOS NO PAINEL SOURCES:
         * Usamos arrayBuffer em vez de URL.createObjectURL
         * Isso evita a criação de entradas no painel Sources do navegador
         * enquanto mantém a funcionalidade completa.
         */
        const arrayBuffer = await fontBlob.arrayBuffer();
        const fontFace = new FontFace(fontName.replace('.ttf', ''), arrayBuffer);
        
        await fontFace.load();
        document.fonts.add(fontFace);
        
        // Armazena no cache global (sem URL)
        globalFontCache.set(fontName, { fontFace });
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('[loadFontFromCache] Erro ao carregar fonte:', error);
      return false;
    }
  };

  /**
   * Recarrega uma fonte específica do cache
   * Útil quando precisamos forçar a atualização de uma fonte
   */
  const reloadFont = async (fontName: string): Promise<boolean> => {
    try {
      // Remove a fonte do cache global e do documento
      if (globalFontCache.has(fontName)) {
        const { fontFace } = globalFontCache.get(fontName)!;
        document.fonts.delete(fontFace);
        globalFontCache.delete(fontName);
      }
      
      // Recarrega a fonte
      return await loadFontFromCache(fontName);
    } catch (error) {
      console.error('[reloadFont] Erro ao recarregar fonte:', error);
      return false;
    }
  };

  /**
   * Limpa o cache global de fontes e força o recarregamento
   * @param options Opções para controlar o comportamento da limpeza
   * @returns Promise que resolve quando todas as fontes forem recarregadas
   */
  const clearFontCache = async (options: { 
    reloadFonts?: boolean; // Se true, tenta recarregar as fontes após limpar
    onProgress?: (progress: number) => void; // Callback para reportar progresso
  } = {}): Promise<void> => {
    try {
      // Guarda os nomes das fontes que estavam no cache
      const fontNames = Array.from(globalFontCache.keys());
      
      // Limpa o cache global e remove as fontes do documento
      globalFontCache.forEach(({ fontFace }) => {
        document.fonts.delete(fontFace);
      });
      globalFontCache.clear();
      
      // Se não precisar recarregar as fontes, termina aqui
      if (!options.reloadFonts) {
        return;
      }

      // Recarrega cada fonte que estava no cache
      for (let i = 0; i < fontNames.length; i++) {
        const fontName = fontNames[i];
        await reloadFont(fontName);
        
        // Reporta o progresso se o callback foi fornecido
        if (options.onProgress) {
          const progress = Math.round(((i + 1) / fontNames.length) * 100);
          options.onProgress(progress);
        }
      }
    } catch (error) {
      console.error('[clearFontCache] Erro ao limpar cache:', error);
      throw error;
    }
  };

  return {
    loadFontFromCache,
    clearFontCache,
    reloadFont
  };
}
